import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { documentsApi } from '../services/api'
import { formatDateTime, formatRecent } from '../utils/dateUtils'
import { describeFile, formatFileSize, isPdfFile } from '../utils/fileTypes'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import { isReadOnlyUser } from '../utils/readerAccess'
import UserAvatar from '../components/UserAvatar'
import PDFViewer from '../components/PDFViewer'
import DocumentViewer from '../components/DocumentViewer'
import { FileGlyph, DocumentStatusChip } from '../components/documents/FileGlyph'
import {
  ArrowLeft,
  ArrowRight,
  RefreshCw,
  AlertCircle,
  Download,
  Printer,
  Loader2,
  ExternalLink,
  Link2,
  Copy,
  Check,
  Maximize2,
  Minimize2,
  Mail,
  FileStack,
  Upload,
  Calendar,
  HardDrive,
  FileType,
  Hash,
  History,
  Files,
  Inbox,
} from 'lucide-react'

// How long the details panel takes to step aside before the preview widens.
const ASIDE_EXIT_MS = 200

function DocumentDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const [document, setDocument] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [requests, setRequests] = useState([])
  const [requestsLoading, setRequestsLoading] = useState(true)
  const [requestsError, setRequestsError] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [printing, setPrinting] = useState(false)
  const [opening, setOpening] = useState(false)
  const [focusMode, setFocusMode] = useState(false)
  const [asideLeaving, setAsideLeaving] = useState(false)
  const focusTimer = useRef(null)

  const fetchDocument = useCallback(async () => {
    try {
      setLoading(true)
      const res = await documentsApi.get(id)
      setDocument(res.data)
      setError(null)
    } catch (err) {
      console.error('Error fetching document:', err)
      const status = err?.response?.status
      setError(status === 404
        ? { title: 'Document not found', message: 'It may have been removed, or the link is out of date.' }
        : status === 403
          ? { title: 'No access to this document', message: 'It belongs to a different organization.' }
          : { title: 'Document didn’t load', message: 'Check your connection and try again.', retry: true })
    } finally {
      setLoading(false)
    }
  }, [id])

  const fetchRequests = useCallback(async () => {
    try {
      setRequestsLoading(true)
      const res = await documentsApi.getRequests(id)
      setRequests(res.data.requests || [])
      setRequestsError(false)
    } catch (err) {
      console.error('Error fetching requests for document:', err)
      setRequestsError(true)
    } finally {
      setRequestsLoading(false)
    }
  }, [id])

  useEffect(() => {
    setDocument(null)
    fetchDocument()
    fetchRequests()
  }, [fetchDocument, fetchRequests])

  useEffect(() => () => clearTimeout(focusTimer.current), [])

  // Entering focus mode: the panel fades aside first, then the preview takes
  // the full width in one step, so the PDF re-fits once rather than on every
  // frame of a width animation. Leaving it, the panel rises back in.
  const toggleFocus = () => {
    clearTimeout(focusTimer.current)
    if (focusMode) {
      setFocusMode(false)
      setAsideLeaving(false)
      return
    }
    setAsideLeaving(true)
    focusTimer.current = setTimeout(() => setFocusMode(true), ASIDE_EXIT_MS)
  }

  const handleDownload = async () => {
    try {
      setDownloading(true)
      const res = await documentsApi.download(id)
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = window.document.createElement('a')
      link.href = url
      link.setAttribute('download', document?.filename || 'document')
      window.document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Error downloading document:', err)
      toast.error('Download failed', 'The file couldn’t be downloaded. Try again in a moment.')
    } finally {
      setDownloading(false)
    }
  }

  const handlePrint = async () => {
    try {
      setPrinting(true)

      // Try SAS URL first (direct from storage), fallback to backend proxy
      let url
      try {
        const sasRes = await documentsApi.getSasUrl(id, 1)
        if (sasRes.data?.url) {
          url = sasRes.data.url
        }
      } catch {
        // SAS URL failed, fall back to backend proxy
      }

      if (!url) {
        const res = await documentsApi.getPdfBlob(id)
        const blob = new Blob([res.data], { type: 'application/pdf' })
        url = window.URL.createObjectURL(blob)
      }

      const printWindow = window.open(url)
      if (printWindow) {
        printWindow.addEventListener('load', () => {
          printWindow.print()
        })
      }
    } catch (err) {
      console.error('Error printing document:', err)
      toast.error('Print failed', 'The document couldn’t be opened for printing.')
    } finally {
      setPrinting(false)
    }
  }

  // Opens the original file in a new tab. The tab is opened before the await
  // so popup blockers treat it as part of the click.
  const handleOpenOriginal = async () => {
    const tab = window.open('', '_blank')
    if (tab) tab.opener = null
    try {
      setOpening(true)
      let url
      try {
        const sasRes = await documentsApi.getSasUrl(id, 1)
        url = sasRes.data?.url
      } catch {
        // Fall back to streaming it through the backend below.
      }
      if (!url) {
        const res = await documentsApi.download(id)
        url = window.URL.createObjectURL(new Blob([res.data], { type: document?.content_type || 'application/octet-stream' }))
        setTimeout(() => window.URL.revokeObjectURL(url), 60_000)
      }
      if (tab) tab.location.href = url
      else window.open(url, '_blank', 'noopener')
    } catch (err) {
      console.error('Error opening document:', err)
      tab?.close()
      toast.error('Couldn’t open the file', 'Try downloading it instead.')
    } finally {
      setOpening(false)
    }
  }

  if (loading && !document) {
    return <DetailSkeleton />
  }

  if (error) {
    return (
      <div className="mx-auto mt-10 max-w-md text-center animate-rise-in">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 ring-1 ring-red-100 dark:bg-red-900/20 dark:ring-red-900/40">
          <AlertCircle className="h-7 w-7 text-red-500 dark:text-red-400" />
        </div>
        <h1 className="mt-4 text-lg font-semibold text-gray-900 dark:text-gray-100">{error.title}</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{error.message}</p>
        <div className="mt-6 flex items-center justify-center gap-2">
          <Link
            to="/documents"
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3.5 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            <ArrowLeft className="h-4 w-4" />
            All documents
          </Link>
          {error.retry && (
            <button
              type="button"
              onClick={fetchDocument}
              disabled={loading}
              className="inline-flex items-center gap-1.5 rounded-lg bg-navy-800 px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-navy-900 disabled:opacity-60"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Try again
            </button>
          )}
        </div>
      </div>
    )
  }

  if (!document) {
    return null
  }

  const file = describeFile(document.filename, document.content_type)
  const isPdf = isPdfFile(document.filename, document.content_type)
  const busy = downloading || printing || opening

  return (
    <div className="space-y-5">
      {/* The app bar already carries the breadcrumb; this is just the way back. */}
      <button
        type="button"
        onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/documents'))}
        className="group -ml-2 inline-flex h-8 items-center gap-1.5 rounded-lg px-2 text-sm font-medium text-gray-500 transition-colors animate-rise-in hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100"
      >
        <ArrowLeft className="h-4 w-4 transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:-translate-x-0.5" />
        Back
      </button>

      {/* Header */}
      <header
        className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)] animate-rise-in dark:border-gray-800 dark:bg-gray-900"
        style={{ '--rise-delay': '40ms' }}
      >
        <div aria-hidden="true" className={`pointer-events-none absolute inset-y-0 left-0 w-2/3 bg-gradient-to-r to-transparent ${file.wash}`} />
        <div className="relative flex flex-col gap-5 p-5 sm:p-6 lg:flex-row lg:items-center">
          <div className="flex min-w-0 flex-1 items-center gap-4 sm:gap-5">
            <div className="group hidden sm:block">
              <FileGlyph file={file} className="h-[4.75rem] w-[3.75rem]" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl font-bold leading-tight tracking-tight text-gray-900 [overflow-wrap:anywhere] dark:text-gray-50 sm:text-2xl">
                {document.filename}
              </h1>
              <div className="mt-2.5 flex flex-wrap items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <DocumentStatusChip status={document.status} displayName={document.status_display} />
                <span>{file.label}</span>
                <Dot />
                <span className="tabular-nums">{formatFileSize(document.file_size_bytes)}</span>
                <Dot />
                <time dateTime={document.created_at || undefined} title={formatDateTime(document.created_at)}>
                  Uploaded {formatRecent(document.created_at)}
                </time>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 lg:flex-shrink-0">
            <CopyButton
              value={() => window.location.href}
              label="Copy link"
              copiedLabel="Link copied"
              icon={Link2}
              className="h-9 w-9"
            />
            <ActionButton onClick={handleOpenOriginal} disabled={busy} busy={opening} icon={ExternalLink} title="Open the original file in a new tab">
              Open
            </ActionButton>
            {isPdf && (
              <ActionButton onClick={handlePrint} disabled={busy} busy={printing} icon={Printer} title="Print">
                Print
              </ActionButton>
            )}
            <button
              type="button"
              onClick={handleDownload}
              disabled={busy}
              className="inline-flex h-9 items-center gap-2 rounded-lg bg-navy-800 px-4 text-sm font-semibold text-white shadow-sm ring-1 ring-navy-900/20 transition-all duration-200 hover:bg-navy-900 enabled:hover:-translate-y-0.5 enabled:hover:shadow-lg enabled:active:translate-y-0 enabled:active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {downloading ? 'Downloading…' : 'Download'}
            </button>
          </div>
        </div>
      </header>

      {/* Preview + details */}
      <div className={`grid items-start gap-5 ${focusMode ? 'grid-cols-1' : 'lg:grid-cols-[minmax(0,1fr)_21rem] xl:grid-cols-[minmax(0,1fr)_23rem]'}`}>
        <section
          aria-label="Document preview"
          className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)] animate-rise-in dark:border-gray-800 dark:bg-gray-900"
          style={{ '--rise-delay': '90ms' }}
        >
          <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-2.5 dark:border-gray-800">
            <div className="flex min-w-0 items-center gap-2">
              <span className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md ring-1 ring-inset ${file.tile}`}>
                <file.Icon className="h-3.5 w-3.5" />
              </span>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Preview</h2>
            </div>
            <button
              type="button"
              onClick={toggleFocus}
              aria-pressed={focusMode}
              title={focusMode ? 'Show the details panel' : 'Give the preview the full width'}
              className="hidden h-8 items-center gap-1.5 rounded-lg px-2.5 text-[13px] font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100 lg:inline-flex"
            >
              {focusMode ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
              {focusMode ? 'Show details' : 'Focus'}
            </button>
          </div>
          <div className="h-[calc(100vh-17rem)] min-h-[560px] p-2 sm:p-3">
            {isPdf ? (
              <PDFViewer documentId={id} highlightedField={null} onClose={null} fileName={document.filename} />
            ) : (
              <DocumentViewer
                documentId={parseInt(id)}
                document={{
                  id: parseInt(id),
                  filename: document.filename,
                  content_type: document.content_type,
                  file_size_bytes: document.file_size_bytes
                }}
                toast={toast}
              />
            )}
          </div>
        </section>

        {!focusMode && (
          <aside
            data-leaving={asideLeaving}
            className="doc-aside space-y-5 lg:sticky lg:top-[5.5rem]"
          >
            <DetailsCard document={document} file={file} />
            <SourceCard document={document} />
            <RelatedRequests
              requests={requests}
              loading={requestsLoading}
              failed={requestsError}
              onRefresh={fetchRequests}
            />
          </aside>
        )}
      </div>
    </div>
  )
}

// ── Header bits ──────────────────────────────────────────────────────────────

function Dot() {
  return <span aria-hidden="true" className="text-gray-300 dark:text-gray-600">·</span>
}

function ActionButton({ onClick, disabled, busy, icon: Icon, title, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="inline-flex h-9 items-center gap-2 rounded-lg border border-gray-200 bg-white/80 px-3.5 text-sm font-medium text-gray-700 backdrop-blur-sm transition-all duration-200 hover:border-gray-300 hover:bg-white enabled:hover:-translate-y-0.5 enabled:hover:shadow-sm enabled:active:translate-y-0 enabled:active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-800/80 dark:text-gray-200 dark:hover:border-gray-600 dark:hover:bg-gray-800"
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4 text-gray-500 dark:text-gray-400" />}
      {children}
    </button>
  )
}

// Copies `value` (a string, or a function returning one) and swaps its icon
// for a check that pops in, with the label announced to screen readers.
function CopyButton({ value, label, copiedLabel = 'Copied', icon: Icon = Copy, className = 'h-7 w-7', iconClassName = 'h-4 w-4' }) {
  const [copied, setCopied] = useState(false)
  const timer = useRef(null)
  useEffect(() => () => clearTimeout(timer.current), [])

  const copy = async () => {
    const text = typeof value === 'function' ? value() : value
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      // Clipboard API blocked (insecure context): fall back to a hidden field.
      const field = window.document.createElement('textarea')
      field.value = text
      field.setAttribute('readonly', '')
      field.style.position = 'fixed'
      field.style.opacity = '0'
      window.document.body.appendChild(field)
      field.select()
      window.document.execCommand('copy')
      field.remove()
    }
    setCopied(true)
    clearTimeout(timer.current)
    timer.current = setTimeout(() => setCopied(false), 1800)
  }

  return (
    <button
      type="button"
      onClick={copy}
      title={copied ? copiedLabel : label}
      aria-label={copied ? copiedLabel : label}
      className={`relative inline-flex flex-shrink-0 items-center justify-center rounded-lg border transition-all duration-200 active:scale-95 ${
        copied
          ? 'border-emerald-200 bg-emerald-50 text-emerald-600 dark:border-emerald-400/30 dark:bg-emerald-400/10 dark:text-emerald-300'
          : 'border-gray-200 bg-white/80 text-gray-500 hover:border-gray-300 hover:bg-white hover:text-gray-800 dark:border-gray-700 dark:bg-gray-800/80 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100'
      } ${className}`}
    >
      {copied
        ? <Check key="done" className={`animate-copied ${iconClassName}`} />
        : <Icon key="idle" className={`animate-panel-in ${iconClassName}`} />}
      <span className="sr-only" aria-live="polite">{copied ? copiedLabel : ''}</span>
    </button>
  )
}

// ── Side panel ───────────────────────────────────────────────────────────────

function PanelCard({ title, action, delay, children }) {
  return (
    <section
      className="rounded-2xl border border-gray-200 bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)] animate-rise-in dark:border-gray-800 dark:bg-gray-900"
      style={{ '--rise-delay': delay }}
    >
      <div className="flex items-center justify-between gap-2 px-4 pb-1 pt-3.5">
        <h2 className="text-[13px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  )
}

function DetailRow({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-3 px-4 py-2.5">
      <Icon className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" aria-hidden="true" />
      <dt className="w-24 flex-shrink-0 text-sm text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="min-w-0 flex-1 text-sm text-gray-900 dark:text-gray-100">{children}</dd>
    </div>
  )
}

function DetailsCard({ document: doc, file }) {
  const modified = doc.updated_at && doc.updated_at !== doc.created_at
  return (
    <PanelCard title="Details" delay="140ms">
      <dl className="pb-2">
        <DetailRow icon={FileType} label="Type">
          <span className="font-medium">{file.label}</span>
          {doc.content_type && (
            <span className="mt-0.5 block truncate font-mono text-[11px] text-gray-500 dark:text-gray-400" title={doc.content_type}>
              {doc.content_type}
            </span>
          )}
        </DetailRow>
        <DetailRow icon={HardDrive} label="Size">
          <span className="font-medium tabular-nums" title={doc.file_size_bytes != null ? `${Number(doc.file_size_bytes).toLocaleString()} bytes` : undefined}>
            {formatFileSize(doc.file_size_bytes)}
          </span>
        </DetailRow>
        <DetailRow icon={Calendar} label="Uploaded">
          <span className="font-medium">{formatDateTime(doc.created_at)}</span>
          <span className="block text-xs text-gray-500 dark:text-gray-400">{formatRecent(doc.created_at)}</span>
        </DetailRow>
        {modified && (
          <DetailRow icon={History} label="Updated">
            <span className="font-medium">{formatDateTime(doc.updated_at)}</span>
          </DetailRow>
        )}
        <DetailRow icon={Hash} label="Document ID">
          <span className="flex items-center gap-2">
            <span className="font-mono text-[13px] font-medium">{doc.id}</span>
            <CopyButton value={String(doc.id)} label="Copy document ID" copiedLabel="ID copied" className="h-6 w-6 !rounded-md" iconClassName="h-3 w-3" />
          </span>
        </DetailRow>
      </dl>
    </PanelCard>
  )
}

function SourceCard({ document: doc }) {
  const source = doc.email_id
    ? { to: `/emails/${doc.email_id}`, icon: Mail, title: 'Arrived by email', hint: 'Open the email in the queue' }
    : doc.request_id
      ? { to: `/requests/${doc.request_id}`, icon: FileStack, title: `Uploaded to request #${String(doc.request_id).substring(0, 8)}`, hint: 'Open the request' }
      : null

  return (
    <PanelCard title="Source" delay="190ms">
      <div className="p-2 pt-1">
        {source ? (
          <Link
            to={source.to}
            className="group flex items-center gap-3 rounded-xl p-2 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/60"
          >
            <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-navy-50 text-navy-600 ring-1 ring-inset ring-navy-200/70 transition-transform duration-300 group-hover:scale-105 dark:bg-navy-500/10 dark:text-navy-300 dark:ring-navy-400/20">
              <source.icon className="h-4 w-4" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium text-gray-900 dark:text-gray-100">{source.title}</span>
              <span className="block text-xs text-gray-500 dark:text-gray-400">{source.hint}</span>
            </span>
            <ArrowRight className="h-4 w-4 flex-shrink-0 text-gray-300 transition-all duration-300 group-hover:translate-x-0.5 group-hover:text-navy-500 dark:text-gray-600 dark:group-hover:text-navy-300" />
          </Link>
        ) : (
          <div className="flex items-center gap-3 p-2">
            <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
              <Upload className="h-4 w-4" />
            </span>
            <span>
              <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">Uploaded directly</span>
              <span className="block text-xs text-gray-500 dark:text-gray-400">Not attached to an email</span>
            </span>
          </div>
        )}
      </div>
    </PanelCard>
  )
}

// Request status → label and chip colours, matching the Requests page.
const REQUEST_STATUS = {
  pending: ['Pending', 'amber'],
  processing: ['AI Extraction in Progress', 'sky'],
  extracted: ['Extracted', 'indigo'],
  reviewing: ['Editing Extracted Data', 'orange'],
  pending_approval: ['Waiting for Review', 'violet'],
  in_review: ['In Review', 'sky'],
  approved: ['Request Submitted', 'emerald'],
  rejected: ['Rejected', 'rose'],
  failed: ['Submission Failed', 'rose'],
  completed: ['Submission Successful', 'emerald'],
  cancelled: ['Cancelled', 'slate'],
}
const TONE_CHIP = {
  amber: 'bg-amber-50 text-amber-700 ring-amber-600/20 dark:bg-amber-400/10 dark:text-amber-300 dark:ring-amber-400/20',
  sky: 'bg-sky-50 text-sky-700 ring-sky-600/20 dark:bg-sky-400/10 dark:text-sky-300 dark:ring-sky-400/20',
  indigo: 'bg-indigo-50 text-indigo-700 ring-indigo-600/20 dark:bg-indigo-400/10 dark:text-indigo-300 dark:ring-indigo-400/20',
  orange: 'bg-orange-50 text-orange-700 ring-orange-600/20 dark:bg-orange-400/10 dark:text-orange-300 dark:ring-orange-400/20',
  violet: 'bg-violet-50 text-violet-700 ring-violet-600/20 dark:bg-violet-400/10 dark:text-violet-300 dark:ring-violet-400/20',
  emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-400/10 dark:text-emerald-300 dark:ring-emerald-400/20',
  rose: 'bg-rose-50 text-rose-700 ring-rose-600/20 dark:bg-rose-400/10 dark:text-rose-300 dark:ring-rose-400/20',
  slate: 'bg-gray-100 text-gray-600 ring-gray-500/20 dark:bg-gray-400/10 dark:text-gray-300 dark:ring-gray-400/20',
}
const TONE_DOT = {
  amber: 'bg-amber-500', sky: 'bg-sky-500', indigo: 'bg-indigo-500', orange: 'bg-orange-500',
  violet: 'bg-violet-500', emerald: 'bg-emerald-500', rose: 'bg-rose-500', slate: 'bg-gray-400',
}

function RelatedRequests({ requests, loading, failed, onRefresh }) {
  return (
    <PanelCard
      title={
        <span className="inline-flex items-center gap-2">
          Related requests
          {!loading && !failed && (
            <span className="rounded-full bg-gray-100 px-1.5 py-px text-[11px] font-semibold normal-case tracking-normal text-gray-600 tabular-nums animate-panel-in dark:bg-gray-800 dark:text-gray-300">
              {requests.length}
            </span>
          )}
        </span>
      }
      action={
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          aria-label="Refresh related requests"
          title="Refresh"
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:opacity-60 dark:hover:bg-gray-800 dark:hover:text-gray-200"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      }
      delay="240ms"
    >
      <div className="p-2 pt-1">
        {loading && requests.length === 0 ? (
          <div className="space-y-2 p-1">
            {[0, 1].map(i => (
              <div key={i} className="space-y-2 rounded-xl border border-gray-100 p-3 dark:border-gray-800">
                <div className="skeleton h-4 w-24 rounded-full" />
                <div className="skeleton h-4 w-4/5" />
                <div className="skeleton h-3 w-1/2" />
              </div>
            ))}
          </div>
        ) : failed ? (
          <div className="flex items-center gap-2 rounded-xl bg-red-50 px-3 py-2.5 text-sm text-red-700 animate-panel-in dark:bg-red-900/20 dark:text-red-300">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span className="flex-1">Couldn&rsquo;t load requests.</span>
            <button type="button" onClick={onRefresh} className="font-semibold hover:underline">Retry</button>
          </div>
        ) : requests.length === 0 ? (
          <div className="flex flex-col items-center px-4 py-6 text-center animate-panel-in">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 dark:bg-gray-800">
              <Inbox className="h-5 w-5 text-gray-400 dark:text-gray-500" />
            </span>
            <p className="mt-2.5 text-sm font-medium text-gray-900 dark:text-gray-100">No requests yet</p>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Requests created from this document will appear here.</p>
          </div>
        ) : (
          <ul className={`max-h-[26rem] space-y-1.5 overflow-y-auto p-0.5 transition-opacity duration-300 ${loading ? 'opacity-60' : ''}`}>
            {requests.map((req, index) => (
              <li key={req.id} className="animate-rise-in" style={{ '--rise-delay': `${280 + Math.min(index, 8) * 45}ms` }}>
                <RelatedRequestItem request={req} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </PanelCard>
  )
}

function RelatedRequestItem({ request }) {
  const { user } = useAuth()
  const isReader = isReadOnlyUser(user)
  const [label, tone] = REQUEST_STATUS[request.status] || REQUEST_STATUS.pending
  const mode = (request.status === 'completed' || request.status === 'cancelled' || isReader) ? 'view' : 'edit'
  const title = request.title || request.email_subject || `Request #${String(request.id).substring(0, 8)}`
  const createdByName = request.created_by_name || 'Unknown'
  const confidence = request.ai_confidence != null ? Math.round(Math.min(100, Math.max(0, request.ai_confidence))) : 0
  const extracting = request.status === 'processing'
  const barClass = confidence >= 90 ? 'bg-emerald-500' : confidence >= 70 ? 'bg-amber-500' : 'bg-rose-500'
  const otherFiles = Math.max(0, (request.document_count || 0) - 1)

  return (
    <Link
      to={`/requests/${request.id}?mode=${mode}`}
      className="group block rounded-xl border border-gray-100 p-3 transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:border-gray-200 hover:bg-gray-50/70 hover:shadow-[0_8px_20px_-14px_rgba(16,24,40,0.35)] motion-safe:hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500/50 dark:border-gray-800 dark:hover:border-gray-700 dark:hover:bg-gray-800/50"
    >
      <div className="flex items-center justify-between gap-2">
        <span className={`inline-flex min-w-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${TONE_CHIP[tone]}`}>
          <span className="relative flex h-1.5 w-1.5 flex-shrink-0">
            {(request.status === 'pending' || extracting) && <span className={`absolute inset-0 rounded-full opacity-60 motion-safe:animate-ping ${TONE_DOT[tone]}`} />}
            <span className={`relative h-1.5 w-1.5 rounded-full ${TONE_DOT[tone]}`} />
          </span>
          <span className="truncate">{label}</span>
        </span>
        <time dateTime={request.created_at || undefined} title={formatDateTime(request.created_at)} className="flex-shrink-0 text-[11px] text-gray-400 dark:text-gray-500">
          {formatRecent(request.created_at)}
        </time>
      </div>

      <p className="mt-2 flex items-center gap-1 text-sm font-semibold text-gray-900 transition-colors group-hover:text-navy-700 dark:text-gray-100 dark:group-hover:text-white">
        <span className="truncate">{title}</span>
        <ArrowRight className="h-3.5 w-3.5 flex-shrink-0 -translate-x-1 text-navy-500 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100 dark:text-navy-300" aria-hidden="true" />
      </p>

      <div className="mt-2.5 flex items-center justify-between gap-3">
        <span className="flex min-w-0 items-center gap-1.5">
          <UserAvatar user={{ id: request.created_by, username: createdByName }} size="sm" className="!h-5 !w-5 !text-[9px]" />
          <span className="truncate text-xs text-gray-600 dark:text-gray-400">{createdByName}</span>
          {otherFiles > 0 && (
            <span className="flex-shrink-0 text-xs text-gray-400 dark:text-gray-500" title={(request.document_names || []).join('\n') || undefined}>
              · +{otherFiles} file{otherFiles !== 1 ? 's' : ''}
            </span>
          )}
        </span>
        <span className="flex flex-shrink-0 items-center gap-1.5" title={extracting ? 'Extraction in progress' : confidence > 0 ? `AI confidence ${confidence}%` : 'No confidence score yet'}>
          {extracting ? (
            <span className="text-[11px] font-medium text-sky-600 dark:text-sky-300">Extracting…</span>
          ) : confidence > 0 ? (
            <>
              <span className="h-1.5 w-12 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                <span className={`block h-full origin-left rounded-full animate-bar-grow ${barClass}`} style={{ width: `${confidence}%` }} />
              </span>
              <span className="w-8 text-right text-[11px] font-semibold tabular-nums text-gray-600 dark:text-gray-300">{confidence}%</span>
            </>
          ) : (
            <span className="text-[11px] text-gray-400 dark:text-gray-500">—</span>
          )}
        </span>
      </div>
    </Link>
  )
}

// ── Loading ──────────────────────────────────────────────────────────────────

// Mirrors the loaded layout so nothing jumps when the document arrives.
function DetailSkeleton() {
  return (
    <div className="space-y-5" aria-busy="true" aria-label="Loading document">
      <div className="flex items-center gap-2">
        <div className="skeleton h-8 w-8 rounded-lg" />
        <div className="skeleton h-4 w-20" />
        <div className="skeleton h-4 w-40" />
      </div>
      <div className="flex items-center gap-5 rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
        <div className="skeleton hidden h-[4.75rem] w-[3.75rem] rounded-lg sm:block" />
        <div className="flex-1 space-y-3">
          <div className="skeleton h-6 w-2/3 max-w-md" />
          <div className="skeleton h-4 w-1/2 max-w-xs" />
        </div>
        <div className="hidden gap-2 lg:flex">
          <div className="skeleton h-9 w-9 rounded-lg" />
          <div className="skeleton h-9 w-20 rounded-lg" />
          <div className="skeleton h-9 w-28 rounded-lg" />
        </div>
      </div>
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_21rem] xl:grid-cols-[minmax(0,1fr)_23rem]">
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
          <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3 dark:border-gray-800">
            <div className="skeleton h-6 w-6 rounded-md" />
            <div className="skeleton h-4 w-16" />
          </div>
          <div className="flex h-[calc(100vh-17rem)] min-h-[560px] items-center justify-center bg-gray-50 dark:bg-gray-800/40">
            <div className="flex flex-col items-center gap-3 text-gray-400 dark:text-gray-500">
              <Files className="h-8 w-8 animate-pulse" />
              <span className="text-sm">Loading preview…</span>
            </div>
          </div>
        </div>
        <div className="space-y-5">
          {[5, 1, 2].map((rows, i) => (
            <div key={i} className="space-y-3 rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
              <div className="skeleton h-3 w-20" />
              {[...Array(rows)].map((_, r) => <div key={r} className="skeleton h-4 w-full" />)}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default DocumentDetail
